// RestaurantInfoEditor.jsx
import React, { useMemo, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import axios from "axios";
import {
  Tabs,
  Tab,
  Card,
  Row,
  Col,
  Form,
  Button,
  Badge,
  Table,
  Modal,
  Alert,
} from "react-bootstrap";

import {
  FaInfoCircle,
  FaConciergeBell,
  FaGavel,
  FaMapMarkerAlt,
  FaImages,
  FaUtensils,
  FaGripVertical,
  FaArrowUp,
  FaArrowDown,
  FaEye,
  FaEdit,
  FaTrashAlt,
  FaLayerGroup,
  FaQuestionCircle,
} from "react-icons/fa";

/**
 * RestaurantInfoEditor
 * - Stores everything under config.website (json) to power dedicated restaurant micro-site.
 *
 * config.website = {
 *   sectionOrder: ["about","highlights","menu","gallery","faq","policies","contact"],
 *   about: { enabled, nameHtml, storyHtml },
 *   highlights: { enabled, title, items: [{key,label,order,enabled}] },
 *   menu: { enabled, title, subtitle, menuUrl, ctaLabel },
 *   gallery: { enabled, title, subtitle, images: [{id,key,alt,title,tag,order,enabled}] },
 *   faq: { enabled, title, description, savedOnce, items: [{id,question,answer,enabled,order}] },
 *   policies: { enabled, title, items: [{key,label,type,enabled,descriptionHtml,order}] },
 *   contact: { enabled, phone, whatsapp, email, website, address, mapUrl },
 * }
 */

// ✅ Reuse same endpoints as Booking Engine (requested)
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000/api";
const CDN_URL = (process.env.REACT_APP_CDN_URL || "").replace(/\/$/, "");

const UPLOAD_GALLERY_ENDPOINT = `${API_URL}/pos/reservations/upload-gallery-image`;
const DELETE_GALLERY_ENDPOINT = `${API_URL}/pos/reservations/gallery-image`;

// Recommended FAQs: NOT loaded by default. Shown as “Add Recommended” only if user has never added/saved FAQs.
const RECOMMENDED_FAQS = [
  {
    question: "How do I book a table?",
    answer:
      "Select your preferred date, time, and party size, then confirm. You’ll receive instant confirmation on screen.",
  },
  {
    question: "Can I modify or cancel my reservation?",
    answer:
      "Yes, if modification/cancellation is enabled by the restaurant. Use the manage link (if provided) or contact us using the details on this page.",
  },
  {
    question: "What is your cancellation / no-show policy?",
    answer:
      "Please cancel in advance if you can’t make it. No-shows may impact future booking availability (as per restaurant policy).",
  },
  {
    question: "How long will you hold my table?",
    answer:
      "We typically hold tables for a short grace period. If you’re running late, please call us so we can assist.",
  },
  {
    question: "Do you accept special requests or dietary preferences?",
    answer:
      "Yes. Add your request during booking (allergies, celebrations, seating preference). We’ll do our best, subject to availability.",
  },
];

const DEFAULT_HIGHLIGHTS = [
  { key: "cuisine", label: "Multi-cuisine" },
  { key: "signature", label: "Signature dishes" },
  { key: "bar", label: "Bar / Cocktails" },
  { key: "outdoor", label: "Outdoor seating" },
  { key: "private", label: "Private dining" },
  { key: "family", label: "Family friendly" },
  { key: "live", label: "Live music" },
  { key: "parking", label: "Parking" },
];

const DEFAULT_POLICIES = [
  {
    key: "cancellation",
    label: "Cancellation",
    defaultDescriptionHtml:
      "<ul><li>You can cancel up to 2 hours before your reservation.</li><li>No-shows may be restricted from future bookings.</li></ul>",
  },
  {
    key: "arrival",
    label: "Arrival & Holding Time",
    defaultDescriptionHtml:
      "<ul><li>We hold tables for 15 minutes after the reserved time.</li><li>If you are running late, please call us.</li></ul>",
  },
  {
    key: "largeGroups",
    label: "Large Groups",
    defaultDescriptionHtml:
      "<ul><li>For parties above a certain size, we may request an advance confirmation.</li><li>Seating arrangement depends on availability.</li></ul>",
  },
  {
    key: "specialRequests",
    label: "Special Requests",
    defaultDescriptionHtml:
      "<ul><li>Dietary preferences are best shared in the Special Requests field.</li><li>We’ll try our best, subject to availability.</li></ul>",
  },
];

const DEFAULT_SECTION_ORDER = [
  "about",
  "highlights",
  "menu",
  "gallery",
  "faq",
  "policies",
  "contact",
];

const SECTION_META = [
  { key: "about", label: "About", hint: "Story & positioning", icon: <FaInfoCircle className="me-2" /> },
  { key: "highlights", label: "Highlights", hint: "Chips / bullets", icon: <FaConciergeBell className="me-2" /> },
  { key: "menu", label: "Menu", hint: "CTA + link (PDF/Web)", icon: <FaUtensils className="me-2" /> },
  { key: "gallery", label: "Gallery", hint: "Photos sell the experience", icon: <FaImages className="me-2" /> },
  { key: "faq", label: "FAQs", hint: "Questions & answers", icon: <FaQuestionCircle className="me-2" /> },
  { key: "policies", label: "Policies", hint: "Trust & clarity", icon: <FaGavel className="me-2" /> },
  { key: "contact", label: "Contact & Location", hint: "Footer + directions", icon: <FaMapMarkerAlt className="me-2" /> },
];

const createDefaultWebsite = () => ({
  sectionOrder: DEFAULT_SECTION_ORDER,

  about: {
    enabled: true,
    nameHtml: "<h2>About the Restaurant</h2>",
    storyHtml:
      "<p>Share your story: cuisine, inspiration, chef highlights, ambience and what makes you special.</p>",
  },

  highlights: {
    enabled: true,
    title: "Highlights",
    items: [],
  },

  menu: {
    enabled: true,
    title: "Menu",
    subtitle: "Explore our menu before you book.",
    menuUrl: "",
    ctaLabel: "View Menu",
  },

  gallery: {
    enabled: true,
    title: "Gallery",
    subtitle: "",
    images: [],
  },

  faq: {
    enabled: true,
    title: "Frequently Asked Questions",
    description: "Quick answers to help guests book with confidence.",
    savedOnce: false, // ✅ once user adds FAQs, we set true, and “Add Recommended” never shows again
    items: [], // ✅ do NOT load defaults automatically
  },

  policies: {
    enabled: true,
    title: "Reservation Policies",
    items: [],
  },

  contact: {
    enabled: true,
    phone: "",
    whatsapp: "",
    email: "",
    website: "",
    address: "",
    mapUrl: "",
  },
});

function safeClone(obj) {
  try {
    if (typeof structuredClone === "function") return structuredClone(obj);
  } catch (_) {}
  return JSON.parse(JSON.stringify(obj || {}));
}

function sortByOrder(list) {
  return (list || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

function reorderArray(list, fromIdx, toIdx) {
  const arr = (list || []).slice();
  if (fromIdx === toIdx) return arr;
  const [moved] = arr.splice(fromIdx, 1);
  arr.splice(toIdx, 0, moved);
  return arr;
}

function normalizeOrders(items) {
  return (items || []).map((it, i) => ({ ...it, order: i }));
}

function makeId(prefix = "id") {
  return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2, 10)}`;
}

function isExternalUrl(key) {
  const v = (key || "").toLowerCase().trim();
  return v.startsWith("http://") || v.startsWith("https://") || v.startsWith("data:");
}

function isImageKeyValid(key) {
  if (!key || !key.trim()) return false;
  const lowered = key.toLowerCase().trim();
  if (isExternalUrl(lowered)) return true;
  return (
    lowered.endsWith(".jpg") ||
    lowered.endsWith(".jpeg") ||
    lowered.endsWith(".png") ||
    lowered.endsWith(".webp") ||
    lowered.endsWith(".gif")
  );
}

function toCdnUrl(key, cdnBaseUrl) {
  if (!key) return "";
  if (isExternalUrl(key)) return key;
  const base = (cdnBaseUrl || CDN_URL || "").replace(/\/$/, "");
  if (!base) return key;
  return `${base}/${key.replace(/^\//, "")}`;
}

export default function RestaurantInfoEditor({ config, onChange, cdnBaseUrl }) {
  const token = localStorage.getItem("token");

  const authHeaders = useMemo(
    () => ({
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
      },
    }),
    [token]
  );

  // ✅ never mutate original; ensure nodes exist
  const updateConfig = (mutateWebsiteFn) => {
    const next = safeClone(config);
    const base = createDefaultWebsite();
    next.website = next.website || {};

    next.website = {
      ...base,
      ...(next.website || {}),
      about: { ...base.about, ...(next.website.about || {}) },
      highlights: { ...base.highlights, ...(next.website.highlights || {}) },
      menu: { ...base.menu, ...(next.website.menu || {}) },
      gallery: { ...base.gallery, ...(next.website.gallery || {}) },
      faq: { ...base.faq, ...(next.website.faq || {}) },
      policies: { ...base.policies, ...(next.website.policies || {}) },
      contact: { ...base.contact, ...(next.website.contact || {}) },
      sectionOrder:
        Array.isArray(next.website.sectionOrder) && next.website.sectionOrder.length
          ? next.website.sectionOrder
          : base.sectionOrder,
    };

    mutateWebsiteFn(next.website);
    onChange(next);
  };

  const website = useMemo(() => {
    const base = createDefaultWebsite();
    const w = config?.website || {};
    return {
      ...base,
      ...w,
      about: { ...base.about, ...(w.about || {}) },
      highlights: { ...base.highlights, ...(w.highlights || {}) },
      menu: { ...base.menu, ...(w.menu || {}) },
      gallery: { ...base.gallery, ...(w.gallery || {}) },
      faq: { ...base.faq, ...(w.faq || {}) },
      policies: { ...base.policies, ...(w.policies || {}) },
      contact: { ...base.contact, ...(w.contact || {}) },
      sectionOrder:
        Array.isArray(w.sectionOrder) && w.sectionOrder.length ? w.sectionOrder : base.sectionOrder,
    };
  }, [config]);

  // Quill toolbars (same style as your PropertyInfo)
  const richTextModules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ["bold", "italic", "underline"],
          [{ list: "ordered" }, { list: "bullet" }],
          ["link"],
          ["clean"],
        ],
      },
    }),
    []
  );

  const simpleModules = useMemo(
    () => ({
      toolbar: { container: [["bold"], [{ list: "bullet" }], ["clean"]] },
    }),
    []
  );

  // ============================
  // SECTION ORDER (drag + toggle)
  // ============================
  const orderedSectionKeys = website.sectionOrder
    .filter((k) => SECTION_META.some((s) => s.key === k))
    .concat(SECTION_META.map((s) => s.key).filter((k) => !website.sectionOrder.includes(k)));

  const [dragSectionKey, setDragSectionKey] = useState(null);

  const reorderSections = (fromKey, toKey) => {
    if (!fromKey || !toKey || fromKey === toKey) return;
    updateConfig((w) => {
      const current = (w.sectionOrder || DEFAULT_SECTION_ORDER).slice();
      const fromIdx = current.indexOf(fromKey);
      const toIdx = current.indexOf(toKey);
      if (fromIdx === -1 || toIdx === -1) return;
      w.sectionOrder = reorderArray(current, fromIdx, toIdx);
    });
  };

  const toggleSectionEnabled = (key, enabled) => {
    updateConfig((w) => {
      w[key] = w[key] || {};
      w[key].enabled = enabled;
    });
  };

  // ============================
  // HIGHLIGHTS
  // ============================
  const highlightsSorted = sortByOrder(website.highlights?.items);
  const [selectedHighlightKey, setSelectedHighlightKey] = useState("");
  const [newHighlightName, setNewHighlightName] = useState("");
  const [dragHighlightIdx, setDragHighlightIdx] = useState(null);

  const availableDefaultHighlights = DEFAULT_HIGHLIGHTS.filter(
    (d) => !highlightsSorted.some((x) => x.key === d.key)
  );

  const addDefaultHighlight = () => {
    if (!selectedHighlightKey) return;
    const def = DEFAULT_HIGHLIGHTS.find((d) => d.key === selectedHighlightKey);
    if (!def) return;

    updateConfig((w) => {
      w.highlights.items = w.highlights.items || [];
      w.highlights.items.push({
        key: def.key,
        label: def.label,
        enabled: true,
        order: w.highlights.items.length,
      });
      w.highlights.items = normalizeOrders(sortByOrder(w.highlights.items));
    });

    setSelectedHighlightKey("");
  };

  const addCustomHighlight = () => {
    const name = newHighlightName.trim();
    if (!name) return;

    const key = `custom_${name.toLowerCase().replace(/[^a-z0-9]+/g, "_").slice(0, 40)}`;

    updateConfig((w) => {
      w.highlights.items = w.highlights.items || [];
      w.highlights.items.push({
        key,
        label: name,
        enabled: true,
        order: w.highlights.items.length,
      });
      w.highlights.items = normalizeOrders(sortByOrder(w.highlights.items));
    });

    setNewHighlightName("");
  };

  const updateHighlight = (idx, patch) => {
    updateConfig((w) => {
      const sorted = sortByOrder(w.highlights.items);
      sorted[idx] = { ...sorted[idx], ...patch };
      w.highlights.items = normalizeOrders(sorted);
    });
  };

  const moveHighlight = (fromIdx, dir) => {
    updateConfig((w) => {
      const sorted = sortByOrder(w.highlights.items);
      const toIdx = fromIdx + dir;
      if (toIdx < 0 || toIdx >= sorted.length) return;
      w.highlights.items = normalizeOrders(reorderArray(sorted, fromIdx, toIdx));
    });
  };

  // ============================
  // POLICIES
  // ============================
  const policiesSorted = sortByOrder(website.policies?.items);
  const [selectedPolicyKey, setSelectedPolicyKey] = useState("");
  const [newPolicyName, setNewPolicyName] = useState("");
  const [editingPolicyIndex, setEditingPolicyIndex] = useState(null);
  const [dragPolicyIdx, setDragPolicyIdx] = useState(null);

  const availableDefaultPolicies = DEFAULT_POLICIES.filter(
    (d) => !policiesSorted.some((x) => x.key === d.key)
  );

  const addDefaultPolicy = () => {
    if (!selectedPolicyKey) return;
    const def = DEFAULT_POLICIES.find((d) => d.key === selectedPolicyKey);
    if (!def) return;

    updateConfig((w) => {
      w.policies.items = w.policies.items || [];
      w.policies.items.push({
        key: def.key,
        label: def.label,
        type: "default",
        enabled: true,
        descriptionHtml: def.defaultDescriptionHtml,
        order: w.policies.items.length,
      });
      w.policies.items = normalizeOrders(sortByOrder(w.policies.items));
    });

    setSelectedPolicyKey("");
  };

  const addCustomPolicy = () => {
    const name = newPolicyName.trim();
    if (!name) return;

    const key = `custom_${name.toLowerCase().replace(/[^a-z0-9]+/g, "_").slice(0, 40)}`;

    updateConfig((w) => {
      w.policies.items = w.policies.items || [];
      w.policies.items.push({
        key,
        label: name,
        type: "custom",
        enabled: true,
        descriptionHtml: "<ul><li>Describe this policy in simple bullet points.</li></ul>",
        order: w.policies.items.length,
      });
      w.policies.items = normalizeOrders(sortByOrder(w.policies.items));
    });

    setNewPolicyName("");
  };

  const updatePolicy = (idx, patch) => {
    updateConfig((w) => {
      const sorted = sortByOrder(w.policies.items);
      sorted[idx] = { ...sorted[idx], ...patch };
      w.policies.items = normalizeOrders(sorted);
    });
  };

  const movePolicy = (fromIdx, dir) => {
    updateConfig((w) => {
      const sorted = sortByOrder(w.policies.items);
      const toIdx = fromIdx + dir;
      if (toIdx < 0 || toIdx >= sorted.length) return;
      w.policies.items = normalizeOrders(reorderArray(sorted, fromIdx, toIdx));
    });
  };

  // ============================
  // FAQS (NEW) — defaults optional (Add Recommended)
  // ============================
  const faqsSorted = sortByOrder(website.faq?.items);
  const [dragFaqIdx, setDragFaqIdx] = useState(null);
  const [newFaqQuestion, setNewFaqQuestion] = useState("");
  const [newFaqAnswer, setNewFaqAnswer] = useState("");

  const showFaqRecommendations = !website.faq?.savedOnce && faqsSorted.length === 0;

  const addRecommendedFaqs = () => {
    updateConfig((w) => {
      w.faq = w.faq || {};
      w.faq.items = w.faq.items || [];
      if (w.faq.items.length) return;

      w.faq.items = normalizeOrders(
        RECOMMENDED_FAQS.map((x, i) => ({
          id: makeId("faq"),
          question: x.question,
          answer: x.answer,
          enabled: true,
          order: i,
        }))
      );

      w.faq.savedOnce = true; // ✅ recommendation never shown again even if later emptied
    });
  };

  const addCustomFaq = () => {
    const q = newFaqQuestion.trim();
    const a = newFaqAnswer.trim();
    if (!q || !a) return;

    updateConfig((w) => {
      w.faq = w.faq || {};
      w.faq.items = w.faq.items || [];
      w.faq.items.push({
        id: makeId("faq"),
        question: q,
        answer: a,
        enabled: true,
        order: w.faq.items.length,
      });
      w.faq.items = normalizeOrders(sortByOrder(w.faq.items));
      w.faq.savedOnce = true; // ✅ once user adds any FAQ, hide recommended forever
    });

    setNewFaqQuestion("");
    setNewFaqAnswer("");
  };

  const updateFaq = (idx, patch) => {
    updateConfig((w) => {
      w.faq = w.faq || {};
      const sorted = sortByOrder(w.faq.items);
      sorted[idx] = { ...sorted[idx], ...patch };
      w.faq.items = normalizeOrders(sorted);
    });
  };

  const moveFaq = (fromIdx, dir) => {
    updateConfig((w) => {
      w.faq = w.faq || {};
      const sorted = sortByOrder(w.faq.items);
      const toIdx = fromIdx + dir;
      if (toIdx < 0 || toIdx >= sorted.length) return;
      w.faq.items = normalizeOrders(reorderArray(sorted, fromIdx, toIdx));
    });
  };

  // ============================
  // GALLERY (PropertyInfo style)
  // ============================
  const gallerySorted = sortByOrder(website.gallery?.images);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [galleryDeleting, setGalleryDeleting] = useState({});
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState("");
  const [showGalleryAddModal, setShowGalleryAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editImage, setEditImage] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, key: null, idx: null });
  const [dragGalleryIdx, setDragGalleryIdx] = useState(null);

  const [draftImage, setDraftImage] = useState({
    title: "",
    key: "",
    alt: "",
    tag: "",
    file: null,
    uploading: false,
    previewUrl: "",
    error: "",
  });

  const getImageUrl = (imgOrDraft) =>
    toCdnUrl(imgOrDraft?.key, cdnBaseUrl) || imgOrDraft?.previewUrl || "";

  const handleGalleryUpload = async (file) => {
    if (!file) return null;
    setGalleryUploading(true);
    const formData = new FormData();
    formData.append("image", file);

    try {
      const res = await axios.post(UPLOAD_GALLERY_ENDPOINT, formData, authHeaders);
      return res.data.fileKey || res.data.key || res.data.url;
    } finally {
      setGalleryUploading(false);
    }
  };

  const openAddGalleryModal = () => {
    setDraftImage({
      title: "",
      key: "",
      alt: "",
      tag: "",
      file: null,
      uploading: false,
      previewUrl: "",
      error: "",
    });
    setShowGalleryAddModal(true);
  };

  const closeAddGalleryModal = () => {
    if (draftImage.uploading) return;
    setShowGalleryAddModal(false);
  };

  const handleDraftFileChange = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      setDraftImage((prev) => ({
        ...prev,
        file,
        previewUrl: e.target.result,
        error: "",
      }));
    };
    reader.readAsDataURL(file);
  };

  const addDraftImageToGallery = async () => {
    const hasManualKey = !!draftImage.key.trim();
    const hasFile = !!draftImage.file;

    if (!hasManualKey && !hasFile) {
      setDraftImage((prev) => ({
        ...prev,
        error: "Upload an image or provide a CDN key / URL to add this to the gallery.",
      }));
      return;
    }

    let keyToUse = draftImage.key.trim();

    if (!keyToUse && hasFile) {
      setDraftImage((prev) => ({ ...prev, uploading: true, error: "" }));
      try {
        const fileKey = await handleGalleryUpload(draftImage.file);
        keyToUse = fileKey;
      } catch (e) {
        setDraftImage((prev) => ({
          ...prev,
          uploading: false,
          error: "Upload failed. Please try again.",
        }));
        return;
      }
    }

    if (!isImageKeyValid(keyToUse)) {
      setDraftImage((prev) => ({
        ...prev,
        uploading: false,
        error: "Please provide a valid image URL/key (.jpg, .jpeg, .png, .webp) or a data URL.",
      }));
      return;
    }

    updateConfig((w) => {
      w.gallery.images = w.gallery.images || [];
      w.gallery.images.push({
        id: makeId("img"),
        enabled: true,
        title: draftImage.title.trim(),
        key: keyToUse,
        alt: draftImage.alt.trim(),
        tag: draftImage.tag.trim(),
        order: w.gallery.images.length,
      });
      w.gallery.images = normalizeOrders(sortByOrder(w.gallery.images));
    });

    setShowGalleryAddModal(false);
    setDraftImage({
      title: "",
      key: "",
      alt: "",
      tag: "",
      file: null,
      uploading: false,
      previewUrl: "",
      error: "",
    });
  };

  const updateImage = (idx, patch) => {
    updateConfig((w) => {
      const sorted = sortByOrder(w.gallery.images);
      sorted[idx] = { ...sorted[idx], ...patch };
      w.gallery.images = normalizeOrders(sorted);
    });
  };

  const moveGalleryImage = (fromIdx, dir) => {
    updateConfig((w) => {
      const sorted = sortByOrder(w.gallery.images);
      const toIdx = fromIdx + dir;
      if (toIdx < 0 || toIdx >= sorted.length) return;
      w.gallery.images = normalizeOrders(reorderArray(sorted, fromIdx, toIdx));
    });
  };

  const openPreview = (img) => {
    const url = toCdnUrl(img?.key, cdnBaseUrl);
    if (!url) return;
    setPreviewImageUrl(url);
    setShowPreviewModal(true);
  };

  const handleDeleteGalleryImage = async () => {
    const { key, idx } = confirmDelete;
    if (idx === null || idx === undefined) return;

    const external = isExternalUrl(key);
    setGalleryDeleting((prev) => ({ ...prev, [idx]: true }));

    // Fast UX: remove from config immediately
    updateConfig((w) => {
      const sorted = sortByOrder(w.gallery.images);
      sorted.splice(idx, 1);
      w.gallery.images = normalizeOrders(sorted);
    });

    if (!external && key) {
      try {
        await axios.delete(DELETE_GALLERY_ENDPOINT, {
          data: { imageKey: key },
          ...authHeaders,
        });
      } catch (_) {
        // don’t block UI
      }
    }

    setGalleryDeleting((prev) => ({ ...prev, [idx]: false }));
    setConfirmDelete({ open: false, key: null, idx: null });
  };

  // ============================
  // Shared UI helper
  // ============================
  const sectionHeader = (title, enabled, onToggle, badgeText) => (
    <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
      <div className="d-flex flex-column flex-sm-row gap-2 align-items-start align-items-sm-center">
        <Form.Label className="mb-0 fw-semibold">{title}</Form.Label>
        {badgeText ? (
          <Badge bg="light" text="dark">
            {badgeText}
          </Badge>
        ) : null}
      </div>
      <Form.Check
        type="switch"
        label={enabled ? "Visible on site" : "Hidden"}
        checked={!!enabled}
        onChange={(e) => onToggle(e.target.checked)}
      />
    </div>
  );

  // ============================
  // Render
  // ============================
  return (
    <Card className="border-0 shadow-sm">
      <Card.Body>
        <Tabs defaultActiveKey="sections" className="mb-0" mountOnEnter={false} unmountOnExit={false}>
          {/* =========================
              SECTIONS ORDER
          ========================== */}
          <Tab
            eventKey="sections"
            title={
              <>
                <FaLayerGroup className="me-2" />
                Sections & Order
              </>
            }
          >
            <div className="pt-3">
              <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
                <div>
                  <div className="fw-semibold">Website Sections</div>
                  <div className="text-muted" style={{ fontSize: 13 }}>
                    Drag to reorder. Toggle to show/hide on the dedicated microsite.
                  </div>
                </div>
              </div>

              <Alert variant="info" className="mb-3">
                This order will be used on the <strong>dedicated restaurant page</strong> (microsite). Widget embed is unaffected.
              </Alert>

              <div className="list-group">
                {orderedSectionKeys.map((key) => {
                  const meta = SECTION_META.find((s) => s.key === key);
                  const enabled = website?.[key]?.enabled ?? true;

                  return (
                    <div
                      key={key}
                      className="list-group-item d-flex align-items-center justify-content-between"
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => reorderSections(dragSectionKey, key)}
                    >
                      <div className="d-flex align-items-center gap-3">
                        <span
                          draggable
                          onDragStart={() => setDragSectionKey(key)}
                          onDragEnd={() => setDragSectionKey(null)}
                          title="Drag to reorder"
                          style={{ cursor: "grab", color: "#6b7280" }}
                        >
                          <FaGripVertical />
                        </span>

                        <div>
                          <div className="fw-semibold d-flex align-items-center">
                            {meta?.icon}
                            <span>{meta?.label || key}</span>
                          </div>
                          <div className="text-muted" style={{ fontSize: 12 }}>
                            {meta?.hint || ""}
                          </div>
                        </div>
                      </div>

                      <div className="d-flex align-items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => {
                            const idx = orderedSectionKeys.indexOf(key);
                            if (idx > 0) reorderSections(key, orderedSectionKeys[idx - 1]);
                          }}
                          title="Move up"
                        >
                          <FaArrowUp />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => {
                            const idx = orderedSectionKeys.indexOf(key);
                            if (idx < orderedSectionKeys.length - 1) reorderSections(key, orderedSectionKeys[idx + 1]);
                          }}
                          title="Move down"
                        >
                          <FaArrowDown />
                        </Button>

                        <Form.Check
                          type="switch"
                          checked={!!enabled}
                          onChange={(e) => toggleSectionEnabled(key, e.target.checked)}
                          label={enabled ? "On" : "Off"}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Tab>

          {/* =========================
              ABOUT
          ========================== */}
          <Tab
            eventKey="about"
            title={
              <>
                <FaInfoCircle className="me-2" />
                About
              </>
            }
          >
            <div className="pt-3">
              {sectionHeader(
                "About the Restaurant",
                website.about?.enabled,
                (v) => toggleSectionEnabled("about", v),
                "Story & positioning"
              )}

              {!website.about?.enabled ? (
                <div className="text-muted small">
                  Turn this section <strong>On</strong> to show your story on the dedicated page.
                </div>
              ) : (
                <Row className="g-3">
                  <Col md={12}>
                    <small className="text-muted d-block mb-2">
                      Keep it short, premium and human. This content appears under the hero.
                    </small>
                  </Col>

                  <Col md={12}>
                    <div className="fw-semibold mb-2">About Title</div>
                    <ReactQuill
                      value={website.about?.nameHtml || ""}
                      onChange={(html) =>
                        updateConfig((w) => {
                          w.about.nameHtml = html;
                        })
                      }
                      modules={richTextModules}
                      theme="snow"
                    />
                  </Col>

                  <Col md={12}>
                    <div className="fw-semibold mb-2">About Story</div>
                    <ReactQuill
                      value={website.about?.storyHtml || ""}
                      onChange={(html) =>
                        updateConfig((w) => {
                          w.about.storyHtml = html;
                        })
                      }
                      modules={richTextModules}
                      theme="snow"
                    />
                  </Col>
                </Row>
              )}
            </div>
          </Tab>

          {/* =========================
              HIGHLIGHTS
          ========================== */}
          <Tab
            eventKey="highlights"
            title={
              <>
                <FaConciergeBell className="me-2" />
                Highlights
              </>
            }
          >
            <div className="pt-3">
              {sectionHeader(
                "Highlights",
                website.highlights?.enabled,
                (v) => toggleSectionEnabled("highlights", v),
                "Shown as icon chips / bullets"
              )}

              {!website.highlights?.enabled ? (
                <div className="text-muted small">
                  Turn this section <strong>On</strong> to show highlights.
                </div>
              ) : (
                <>
                  <Row className="g-3 mb-3">
                    <Col md={6}>
                      <Form.Label>Section Title</Form.Label>
                      <Form.Control
                        value={website.highlights?.title || ""}
                        onChange={(e) =>
                          updateConfig((w) => {
                            w.highlights.title = e.target.value;
                          })
                        }
                        placeholder="Highlights"
                      />
                    </Col>
                  </Row>

                  <Card className="border-0 shadow-sm mb-3">
                    <Card.Body>
                      <div className="fw-semibold mb-2">Quick add highlights</div>
                      <Row className="gy-2">
                        <Col md={6}>
                          <Form.Label className="small">Add from common highlights</Form.Label>
                          <div className="d-flex gap-2">
                            <Form.Select
                              size="sm"
                              value={selectedHighlightKey}
                              onChange={(e) => setSelectedHighlightKey(e.target.value)}
                            >
                              <option value="">Choose a highlight</option>
                              {availableDefaultHighlights.map((h) => (
                                <option key={h.key} value={h.key}>
                                  {h.label}
                                </option>
                              ))}
                            </Form.Select>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              disabled={!selectedHighlightKey}
                              onClick={addDefaultHighlight}
                            >
                              Add
                            </Button>
                          </div>
                        </Col>
                        <Col md={6}>
                          <Form.Label className="small">Add custom highlight</Form.Label>
                          <div className="d-flex gap-2">
                            <Form.Control
                              size="sm"
                              value={newHighlightName}
                              placeholder="e.g. Rooftop seating"
                              onChange={(e) => setNewHighlightName(e.target.value)}
                            />
                            <Button
                              size="sm"
                              variant="outline-success"
                              disabled={!newHighlightName.trim()}
                              onClick={addCustomHighlight}
                            >
                              Add
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  <div className="fw-semibold mb-2">Your highlights (drag to reorder)</div>
                  {highlightsSorted.length === 0 ? (
                    <div className="text-muted small">No highlights yet. Add a few to make the page feel premium.</div>
                  ) : (
                    <Table hover responsive size="sm" className="align-middle">
                      <thead>
                        <tr>
                          <th style={{ width: 56 }} />
                          <th style={{ width: 80 }}>Show</th>
                          <th>Label</th>
                          <th style={{ width: 120 }}>Order</th>
                          <th style={{ width: 140 }} />
                        </tr>
                      </thead>
                      <tbody>
                        {highlightsSorted.map((h, idx) => (
                          <tr
                            key={`${h.key}_${idx}`}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={() => {
                              if (dragHighlightIdx === null || dragHighlightIdx === idx) return;
                              updateConfig((w) => {
                                const sorted = sortByOrder(w.highlights.items);
                                w.highlights.items = normalizeOrders(reorderArray(sorted, dragHighlightIdx, idx));
                              });
                              setDragHighlightIdx(null);
                            }}
                          >
                            <td>
                              <span
                                draggable
                                onDragStart={() => setDragHighlightIdx(idx)}
                                onDragEnd={() => setDragHighlightIdx(null)}
                                style={{ cursor: "grab", color: "#6b7280" }}
                                title="Drag to reorder"
                              >
                                <FaGripVertical />
                              </span>
                            </td>

                            <td>
                              <Form.Check
                                type="switch"
                                checked={!!h.enabled}
                                onChange={(e) => updateHighlight(idx, { enabled: e.target.checked })}
                              />
                            </td>

                            <td>
                              <Form.Control
                                size="sm"
                                value={h.label || ""}
                                onChange={(e) => updateHighlight(idx, { label: e.target.value })}
                              />
                            </td>

                            <td className="text-muted small">#{idx + 1}</td>

                            <td className="text-end">
                              <div className="d-inline-flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  onClick={() => moveHighlight(idx, -1)}
                                  disabled={idx === 0}
                                  title="Up"
                                >
                                  <FaArrowUp />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  onClick={() => moveHighlight(idx, 1)}
                                  disabled={idx === highlightsSorted.length - 1}
                                  title="Down"
                                >
                                  <FaArrowDown />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => {
                                    updateConfig((w) => {
                                      const sorted = sortByOrder(w.highlights.items);
                                      sorted.splice(idx, 1);
                                      w.highlights.items = normalizeOrders(sorted);
                                    });
                                  }}
                                  title="Remove"
                                >
                                  Remove
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </>
              )}
            </div>
          </Tab>

          {/* =========================
              MENU
          ========================== */}
          <Tab
            eventKey="menu"
            title={
              <>
                <FaUtensils className="me-2" />
                Menu (Site)
              </>
            }
          >
            <div className="pt-3">
              {sectionHeader(
                "Menu Section",
                website.menu?.enabled,
                (v) => toggleSectionEnabled("menu", v),
                "CTA + link (PDF / webpage)"
              )}

              {!website.menu?.enabled ? (
                <div className="text-muted small">
                  Turn this section <strong>On</strong> to show menu on the dedicated page.
                </div>
              ) : (
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Label>Title</Form.Label>
                    <Form.Control
                      value={website.menu?.title || ""}
                      onChange={(e) => updateConfig((w) => (w.menu.title = e.target.value))}
                      placeholder="Menu"
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>CTA Label</Form.Label>
                    <Form.Control
                      value={website.menu?.ctaLabel || ""}
                      onChange={(e) => updateConfig((w) => (w.menu.ctaLabel = e.target.value))}
                      placeholder="View Menu"
                    />
                  </Col>
                  <Col md={12}>
                    <Form.Label>Subtitle</Form.Label>
                    <Form.Control
                      value={website.menu?.subtitle || ""}
                      onChange={(e) => updateConfig((w) => (w.menu.subtitle = e.target.value))}
                      placeholder="Explore our menu before you book."
                    />
                  </Col>
                  <Col md={12}>
                    <Form.Label>Menu URL (PDF/Web)</Form.Label>
                    <Form.Control
                      value={website.menu?.menuUrl || ""}
                      onChange={(e) => updateConfig((w) => (w.menu.menuUrl = e.target.value))}
                      placeholder="https://..."
                    />
                    <small className="text-muted">
                      Later we’ll connect to your POS menu data and render sections dynamically.
                    </small>
                  </Col>
                </Row>
              )}
            </div>
          </Tab>

          {/* =========================
              GALLERY
          ========================== */}
          <Tab
            eventKey="gallery"
            title={
              <>
                <FaImages className="me-2" />
                Gallery
              </>
            }
          >
            <div className="pt-3">
              {sectionHeader(
                "Gallery",
                website.gallery?.enabled,
                (v) => toggleSectionEnabled("gallery", v),
                "Photos sell the experience"
              )}

              {!website.gallery?.enabled ? (
                <div className="text-muted small">
                  Turn this section <strong>On</strong> to show a gallery.
                </div>
              ) : (
                <>
                  <Row className="g-3 mb-3">
                    <Col md={6}>
                      <Form.Label>Section Title</Form.Label>
                      <Form.Control
                        value={website.gallery?.title || ""}
                        onChange={(e) => updateConfig((w) => (w.gallery.title = e.target.value))}
                        placeholder="Gallery"
                      />
                    </Col>
                    <Col md={6}>
                      <Form.Label>Subtitle (optional)</Form.Label>
                      <Form.Control
                        value={website.gallery?.subtitle || ""}
                        onChange={(e) => updateConfig((w) => (w.gallery.subtitle = e.target.value))}
                        placeholder="A glimpse of ambience, food & vibe"
                      />
                    </Col>
                  </Row>

                  <Card className="border-0 shadow-sm">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                          <div className="fw-semibold">Gallery Images ({gallerySorted.length})</div>
                          <div className="text-muted" style={{ fontSize: 12 }}>
                            Drag cards to reorder. Use preview/edit/delete actions.
                          </div>
                        </div>

                        <Button size="sm" variant="primary" onClick={openAddGalleryModal} disabled={galleryUploading}>
                          {galleryUploading ? "Uploading..." : "Add Image"}
                        </Button>
                      </div>

                      {gallerySorted.length === 0 ? (
                        <div className="text-center py-5 text-muted">
                          <FaImages size={44} className="mb-2 opacity-25" />
                          <div>No images yet. Click “Add Image”.</div>
                        </div>
                      ) : (
                        <Row xs={2} md={3} lg={4} className="g-4">
                          {gallerySorted.map((img, idx) => (
                            <Col
                              key={img.id || img.key || idx}
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={() => {
                                if (dragGalleryIdx === null || dragGalleryIdx === idx) return;
                                updateConfig((w) => {
                                  const sorted = sortByOrder(w.gallery.images);
                                  w.gallery.images = normalizeOrders(reorderArray(sorted, dragGalleryIdx, idx));
                                });
                                setDragGalleryIdx(null);
                              }}
                            >
                              <Card className="h-100 shadow-sm border-0">
                                <div style={{ position: "relative" }}>
                                  <img
                                    src={toCdnUrl(img.key, cdnBaseUrl)}
                                    alt={img.alt || img.title || ""}
                                    className="card-img-top"
                                    style={{ height: 160, objectFit: "cover" }}
                                  />

                                  <span
                                    draggable
                                    onDragStart={() => setDragGalleryIdx(idx)}
                                    onDragEnd={() => setDragGalleryIdx(null)}
                                    title="Drag to reorder"
                                    style={{
                                      position: "absolute",
                                      top: 10,
                                      left: 10,
                                      width: 30,
                                      height: 30,
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      borderRadius: 10,
                                      background: "rgba(0,0,0,0.45)",
                                      color: "#fff",
                                      cursor: "grab",
                                    }}
                                  >
                                    <FaGripVertical />
                                  </span>
                                </div>

                                <Card.Body className="p-2">
                                  <div className="d-flex justify-content-between align-items-center">
                                    <div style={{ minWidth: 0 }}>
                                      <div
                                        className="fw-semibold"
                                        style={{
                                          fontSize: 13,
                                          whiteSpace: "nowrap",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                        }}
                                      >
                                        {img.title || "Untitled"}
                                      </div>
                                      <div className="text-muted" style={{ fontSize: 12 }}>
                                        {img.tag || "—"}
                                      </div>
                                    </div>

                                    <Form.Check
                                      type="switch"
                                      checked={img.enabled ?? true}
                                      onChange={(e) => updateImage(idx, { enabled: e.target.checked })}
                                      title="Show/Hide"
                                    />
                                  </div>

                                  <div className="d-flex justify-content-center gap-2 mt-2">
                                    <Button variant="light" size="sm" onClick={() => openPreview(img)} title="View">
                                      <FaEye />
                                    </Button>

                                    <Button
                                      variant="light"
                                      size="sm"
                                      onClick={() => {
                                        setEditImage({ ...img, idx });
                                        setShowEditModal(true);
                                      }}
                                      title="Edit"
                                    >
                                      <FaEdit />
                                    </Button>

                                    <Button
                                      variant="light"
                                      size="sm"
                                      className="text-danger"
                                      disabled={galleryDeleting[idx]}
                                      onClick={() => setConfirmDelete({ open: true, key: img.key, idx })}
                                      title="Delete"
                                    >
                                      <FaTrashAlt />
                                    </Button>
                                  </div>

                                  <div className="d-flex justify-content-center gap-2 mt-2">
                                    <Button
                                      size="sm"
                                      variant="outline-secondary"
                                      onClick={() => moveGalleryImage(idx, -1)}
                                      disabled={idx === 0}
                                      title="Move up"
                                    >
                                      <FaArrowUp />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline-secondary"
                                      onClick={() => moveGalleryImage(idx, 1)}
                                      disabled={idx === gallerySorted.length - 1}
                                      title="Move down"
                                    >
                                      <FaArrowDown />
                                    </Button>
                                  </div>
                                </Card.Body>
                              </Card>
                            </Col>
                          ))}
                        </Row>
                      )}
                    </Card.Body>
                  </Card>
                </>
              )}
            </div>
          </Tab>

          {/* =========================
              FAQ (NEW)
          ========================== */}
          <Tab
            eventKey="faq"
            title={
              <>
                <FaQuestionCircle className="me-2" />
                FAQs
              </>
            }
          >
            <div className="pt-3">
              {sectionHeader(
                "FAQs",
                website.faq?.enabled,
                (v) => toggleSectionEnabled("faq", v),
                "Questions & answers for guests"
              )}

              {!website.faq?.enabled ? (
                <div className="text-muted small">
                  Turn this section <strong>On</strong> to show FAQs on the dedicated page.
                </div>
              ) : (
                <>
                  <Row className="g-3 mb-3">
                    <Col md={6}>
                      <Form.Label>FAQ Title (single line)</Form.Label>
                      <Form.Control
                        value={website.faq?.title || ""}
                        onChange={(e) => updateConfig((w) => (w.faq.title = e.target.value))}
                        placeholder="Frequently Asked Questions"
                      />
                    </Col>
                    <Col md={6}>
                      <Form.Label>Description</Form.Label>
                      <Form.Control
                        value={website.faq?.description || ""}
                        onChange={(e) => updateConfig((w) => (w.faq.description = e.target.value))}
                        placeholder="Short line to set context for guests"
                      />
                    </Col>
                  </Row>

                  {showFaqRecommendations ? (
                    <Card className="border-0 shadow-sm mb-3">
                      <Card.Body className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                        <div>
                          <div className="fw-semibold">Recommended FAQs</div>
                          <div className="text-muted" style={{ fontSize: 13 }}>
                            Add a ready set of booking + cancellation FAQs. You can edit them anytime.
                            <br />
                            <strong>Note:</strong> This suggestion disappears permanently once you add FAQs.
                          </div>
                        </div>

                        <div className="d-flex gap-2">
                          <Button variant="outline-primary" onClick={addRecommendedFaqs}>
                            Add Recommended
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  ) : null}

                  <Card className="border-0 shadow-sm mb-3">
                    <Card.Body>
                      <div className="fw-semibold mb-2">Add a new FAQ</div>
                      <Row className="g-3">
                        <Col md={6}>
                          <Form.Label>Question</Form.Label>
                          <Form.Control
                            value={newFaqQuestion}
                            onChange={(e) => setNewFaqQuestion(e.target.value)}
                            placeholder="e.g. Can I cancel my reservation?"
                          />
                        </Col>
                        <Col md={6}>
                          <Form.Label>Answer</Form.Label>
                          <Form.Control
                            value={newFaqAnswer}
                            onChange={(e) => setNewFaqAnswer(e.target.value)}
                            placeholder="Short, clear answer for guests"
                          />
                        </Col>
                        <Col md={12} className="d-flex justify-content-end">
                          <Button
                            variant="primary"
                            onClick={addCustomFaq}
                            disabled={!newFaqQuestion.trim() || !newFaqAnswer.trim()}
                          >
                            Add FAQ
                          </Button>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  <div className="fw-semibold mb-2">Your FAQs (drag to reorder)</div>
                  {faqsSorted.length === 0 ? (
                    <div className="text-muted small">
                      No FAQs yet. Add 4–8 FAQs to reduce booking friction.
                    </div>
                  ) : (
                    <Table hover responsive size="sm" className="align-middle">
                      <thead>
                        <tr>
                          <th style={{ width: 56 }} />
                          <th style={{ width: 80 }}>Show</th>
                          <th>Question</th>
                          <th>Answer</th>
                          <th style={{ width: 120 }}>Order</th>
                          <th style={{ width: 180 }} />
                        </tr>
                      </thead>
                      <tbody>
                        {faqsSorted.map((f, idx) => (
                          <tr
                            key={f.id || idx}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={() => {
                              if (dragFaqIdx === null || dragFaqIdx === idx) return;
                              updateConfig((w) => {
                                w.faq = w.faq || {};
                                const sorted = sortByOrder(w.faq.items);
                                w.faq.items = normalizeOrders(reorderArray(sorted, dragFaqIdx, idx));
                              });
                              setDragFaqIdx(null);
                            }}
                          >
                            <td>
                              <span
                                draggable
                                onDragStart={() => setDragFaqIdx(idx)}
                                onDragEnd={() => setDragFaqIdx(null)}
                                style={{ cursor: "grab", color: "#6b7280" }}
                                title="Drag to reorder"
                              >
                                <FaGripVertical />
                              </span>
                            </td>

                            <td>
                              <Form.Check
                                type="switch"
                                checked={f.enabled ?? true}
                                onChange={(e) => updateFaq(idx, { enabled: e.target.checked })}
                              />
                            </td>

                            <td style={{ minWidth: 220 }}>
                              <Form.Control
                                size="sm"
                                value={f.question || ""}
                                onChange={(e) => updateFaq(idx, { question: e.target.value })}
                              />
                            </td>

                            <td style={{ minWidth: 260 }}>
                              <Form.Control
                                size="sm"
                                value={f.answer || ""}
                                onChange={(e) => updateFaq(idx, { answer: e.target.value })}
                              />
                            </td>

                            <td className="text-muted small">#{idx + 1}</td>

                            <td className="text-end">
                              <div className="d-inline-flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  onClick={() => moveFaq(idx, -1)}
                                  disabled={idx === 0}
                                  title="Up"
                                >
                                  <FaArrowUp />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  onClick={() => moveFaq(idx, 1)}
                                  disabled={idx === faqsSorted.length - 1}
                                  title="Down"
                                >
                                  <FaArrowDown />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => {
                                    updateConfig((w) => {
                                      w.faq = w.faq || {};
                                      const sorted = sortByOrder(w.faq.items);
                                      sorted.splice(idx, 1);
                                      w.faq.items = normalizeOrders(sorted);
                                      // savedOnce stays true (requirement)
                                      w.faq.savedOnce = true;
                                    });
                                  }}
                                  title="Remove"
                                >
                                  Remove
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </>
              )}
            </div>
          </Tab>

          {/* =========================
              POLICIES
          ========================== */}
          <Tab
            eventKey="policies"
            title={
              <>
                <FaGavel className="me-2" />
                Policies
              </>
            }
          >
            <div className="pt-3">
              {sectionHeader(
                "Reservation Policies",
                website.policies?.enabled,
                (v) => toggleSectionEnabled("policies", v),
                "Shown before confirmation"
              )}

              {!website.policies?.enabled ? (
                <div className="text-muted small">
                  Turn this section <strong>On</strong> to manage and show policies.
                </div>
              ) : (
                <>
                  <Row className="g-3 mb-3">
                    <Col md={6}>
                      <Form.Label>Section Title</Form.Label>
                      <Form.Control
                        value={website.policies?.title || ""}
                        onChange={(e) =>
                          updateConfig((w) => {
                            w.policies.title = e.target.value;
                          })
                        }
                        placeholder="Reservation Policies"
                      />
                    </Col>
                  </Row>

                  <Card className="border-0 shadow-sm mb-3">
                    <Card.Body>
                      <div className="fw-semibold mb-2">Quick add policies</div>
                      <Row className="gy-2">
                        <Col md={6}>
                          <Form.Label className="small">Add from common policies</Form.Label>
                          <div className="d-flex gap-2">
                            <Form.Select
                              size="sm"
                              value={selectedPolicyKey}
                              onChange={(e) => setSelectedPolicyKey(e.target.value)}
                            >
                              <option value="">Choose a policy</option>
                              {availableDefaultPolicies.map((p) => (
                                <option key={p.key} value={p.key}>
                                  {p.label}
                                </option>
                              ))}
                            </Form.Select>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              disabled={!selectedPolicyKey}
                              onClick={addDefaultPolicy}
                            >
                              Add
                            </Button>
                          </div>
                        </Col>
                        <Col md={6}>
                          <Form.Label className="small">Add custom policy</Form.Label>
                          <div className="d-flex gap-2">
                            <Form.Control
                              size="sm"
                              value={newPolicyName}
                              placeholder="e.g. Dress code"
                              onChange={(e) => setNewPolicyName(e.target.value)}
                            />
                            <Button
                              size="sm"
                              variant="outline-success"
                              disabled={!newPolicyName.trim()}
                              onClick={addCustomPolicy}
                            >
                              Add
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  {policiesSorted.length === 0 ? (
                    <div className="text-muted small">No policies yet. Add 3–5 simple policies for clarity and trust.</div>
                  ) : (
                    <>
                      <small className="text-muted d-block mb-2">
                        Drag to reorder. Click <strong>Edit</strong> to update bullet points.
                      </small>

                      <Table hover responsive size="sm" className="align-middle">
                        <thead>
                          <tr>
                            <th style={{ width: 56 }} />
                            <th style={{ width: 80 }}>Show</th>
                            <th>Name</th>
                            <th style={{ width: 110 }}>Type</th>
                            <th style={{ width: 110 }}>Text</th>
                            <th style={{ width: 180 }} />
                          </tr>
                        </thead>
                        <tbody>
                          {policiesSorted.map((p, idx) => (
                            <tr
                              key={`${p.key}_${idx}`}
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={() => {
                                if (dragPolicyIdx === null || dragPolicyIdx === idx) return;
                                updateConfig((w) => {
                                  const sorted = sortByOrder(w.policies.items);
                                  w.policies.items = normalizeOrders(reorderArray(sorted, dragPolicyIdx, idx));
                                });
                                setDragPolicyIdx(null);
                              }}
                            >
                              <td>
                                <span
                                  draggable
                                  onDragStart={() => setDragPolicyIdx(idx)}
                                  onDragEnd={() => setDragPolicyIdx(null)}
                                  style={{ cursor: "grab", color: "#6b7280" }}
                                  title="Drag to reorder"
                                >
                                  <FaGripVertical />
                                </span>
                              </td>
                              <td>
                                <Form.Check
                                  type="switch"
                                  checked={!!p.enabled}
                                  onChange={(e) => updatePolicy(idx, { enabled: e.target.checked })}
                                />
                              </td>
                              <td>
                                <Form.Control
                                  size="sm"
                                  value={p.label || ""}
                                  onChange={(e) => updatePolicy(idx, { label: e.target.value })}
                                />
                              </td>
                              <td>
                                <Badge bg={p.type === "custom" ? "success" : "secondary"}>
                                  {p.type || "default"}
                                </Badge>
                              </td>
                              <td>
                                <Button size="sm" variant="outline-primary" onClick={() => setEditingPolicyIndex(idx)}>
                                  Edit
                                </Button>
                              </td>
                              <td className="text-end">
                                <div className="d-inline-flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    onClick={() => movePolicy(idx, -1)}
                                    disabled={idx === 0}
                                    title="Up"
                                  >
                                    <FaArrowUp />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    onClick={() => movePolicy(idx, 1)}
                                    disabled={idx === policiesSorted.length - 1}
                                    title="Down"
                                  >
                                    <FaArrowDown />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline-danger"
                                    onClick={() => {
                                      updateConfig((w) => {
                                        const sorted = sortByOrder(w.policies.items);
                                        sorted.splice(idx, 1);
                                        w.policies.items = normalizeOrders(sorted);
                                      });
                                    }}
                                    title="Remove"
                                  >
                                    Remove
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </>
                  )}
                </>
              )}
            </div>
          </Tab>

          {/* =========================
              CONTACT
          ========================== */}
          <Tab
            eventKey="contact"
            title={
              <>
                <FaMapMarkerAlt className="me-2" />
                Contact & Location
              </>
            }
          >
            <div className="pt-3">
              {sectionHeader(
                "Contact & Location",
                website.contact?.enabled,
                (v) => toggleSectionEnabled("contact", v),
                "Used in footer + contact section"
              )}

              {!website.contact?.enabled ? (
                <div className="text-muted small">
                  Turn this section <strong>On</strong> to show contact details.
                </div>
              ) : (
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Label>Phone</Form.Label>
                    <Form.Control
                      value={website.contact?.phone || ""}
                      onChange={(e) => updateConfig((w) => (w.contact.phone = e.target.value))}
                      placeholder="+91..."
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>WhatsApp</Form.Label>
                    <Form.Control
                      value={website.contact?.whatsapp || ""}
                      onChange={(e) => updateConfig((w) => (w.contact.whatsapp = e.target.value))}
                      placeholder="+91..."
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      value={website.contact?.email || ""}
                      onChange={(e) => updateConfig((w) => (w.contact.email = e.target.value))}
                      placeholder="reservations@..."
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Website</Form.Label>
                    <Form.Control
                      value={website.contact?.website || ""}
                      onChange={(e) => updateConfig((w) => (w.contact.website = e.target.value))}
                      placeholder="https://..."
                    />
                  </Col>
                  <Col md={12}>
                    <Form.Label>Address</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={website.contact?.address || ""}
                      onChange={(e) => updateConfig((w) => (w.contact.address = e.target.value))}
                      placeholder="Full address"
                    />
                  </Col>
                  <Col md={12}>
                    <Form.Label>Google Map URL (optional)</Form.Label>
                    <Form.Control
                      value={website.contact?.mapUrl || ""}
                      onChange={(e) => updateConfig((w) => (w.contact.mapUrl = e.target.value))}
                      placeholder="https://maps.google.com/..."
                    />
                    <small className="text-muted">We’ll show a “Get Directions” link on the dedicated page.</small>
                  </Col>
                </Row>
              )}
            </div>
          </Tab>
        </Tabs>

        {/* =========================
            Policy editor modal
        ========================== */}
        <Modal
          show={editingPolicyIndex !== null}
          onHide={() => setEditingPolicyIndex(null)}
          size="lg"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Edit policy text</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {editingPolicyIndex !== null && policiesSorted[editingPolicyIndex] ? (
              <>
                <div className="text-muted mb-2" style={{ fontSize: 13 }}>
                  Use bullet points. Keep it short and clear.
                </div>
                <ReactQuill
                  value={policiesSorted[editingPolicyIndex].descriptionHtml || ""}
                  onChange={(html) => updatePolicy(editingPolicyIndex, { descriptionHtml: html })}
                  modules={simpleModules}
                  theme="snow"
                />
              </>
            ) : null}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setEditingPolicyIndex(null)}>
              Done
            </Button>
          </Modal.Footer>
        </Modal>

        {/* =========================
            Full image preview
        ========================== */}
        <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} size="lg" centered>
          <Modal.Body className="p-0">
            {previewImageUrl ? (
              <img src={previewImageUrl} alt="Preview" style={{ width: "100%", height: "auto", display: "block" }} />
            ) : null}
          </Modal.Body>
        </Modal>

        {/* =========================
            Gallery Add Image modal
        ========================== */}
        <Modal
          show={showGalleryAddModal}
          onHide={closeAddGalleryModal}
          centered
          size="lg"
          dialogClassName="gallery-upload-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>Add gallery image</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <Row className="gy-3">
              <Col md={5}>
                <Form.Label className="small">Image preview</Form.Label>
                <div
                  style={{
                    border: "1px dashed #d1d5db",
                    borderRadius: 8,
                    padding: 12,
                    textAlign: "center",
                    height: "100%",
                  }}
                >
                  {getImageUrl(draftImage) ? (
                    <img
                      src={draftImage.previewUrl || toCdnUrl(draftImage.key, cdnBaseUrl)}
                      alt={draftImage.alt || draftImage.title || ""}
                      style={{ maxWidth: "100%", maxHeight: 220, objectFit: "cover" }}
                    />
                  ) : (
                    <div className="text-muted small" style={{ minHeight: 120 }}>
                      No image selected yet.
                    </div>
                  )}

                  <Form.Label
                    className="btn btn-outline-primary btn-sm mt-2 mb-0"
                    style={{ cursor: draftImage.uploading ? "not-allowed" : "pointer" }}
                  >
                    {draftImage.uploading ? "Uploading..." : "Choose image file"}
                    <Form.Control
                      type="file"
                      accept="image/*"
                      hidden
                      disabled={draftImage.uploading}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleDraftFileChange(file);
                      }}
                    />
                  </Form.Label>

                  <div className="text-muted small mt-2">
                    <strong>Tip:</strong> Upload from your computer, or paste a CDN key / full URL on the right.
                  </div>
                </div>
              </Col>

              <Col md={7}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Image URL / CDN key <span className="text-muted">(optional)</span>
                  </Form.Label>
                  <Form.Control
                    value={draftImage.key}
                    onChange={(e) => setDraftImage((p) => ({ ...p, key: e.target.value, error: "" }))}
                    placeholder="company_61/table-booking/gallery/xxx.jpg OR https://..."
                  />
                  <div className="text-muted" style={{ fontSize: 12, marginTop: 6 }}>
                    If you upload a file, we will store it to CDN and save the returned key.
                  </div>
                </Form.Group>

                <Row className="g-3">
                  <Col md={12}>
                    <Form.Label>Title</Form.Label>
                    <Form.Control
                      value={draftImage.title}
                      onChange={(e) => setDraftImage((p) => ({ ...p, title: e.target.value }))}
                      placeholder="e.g. Dining Hall"
                    />
                  </Col>
                  <Col md={12}>
                    <Form.Label>Alt text</Form.Label>
                    <Form.Control
                      value={draftImage.alt}
                      onChange={(e) => setDraftImage((p) => ({ ...p, alt: e.target.value }))}
                      placeholder="Accessibility description"
                    />
                  </Col>
                  <Col md={12}>
                    <Form.Label>Tag (optional)</Form.Label>
                    <Form.Control
                      value={draftImage.tag}
                      onChange={(e) => setDraftImage((p) => ({ ...p, tag: e.target.value }))}
                      placeholder="food / ambience / seating"
                    />
                  </Col>
                </Row>

                {draftImage.error ? (
                  <Alert variant="danger" className="mt-3 mb-0">
                    {draftImage.error}
                  </Alert>
                ) : null}
              </Col>
            </Row>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="outline-secondary" onClick={closeAddGalleryModal} disabled={draftImage.uploading}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={addDraftImageToGallery}
              disabled={draftImage.uploading || (!draftImage.key.trim() && !draftImage.file)}
            >
              Add
            </Button>
          </Modal.Footer>
        </Modal>

        {/* =========================
            Edit Image modal
        ========================== */}
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Edit gallery image</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {editImage ? (
              <Row className="g-3">
                <Col md={5}>
                  <div className="text-muted small mb-2">Preview</div>
                  <img
                    src={toCdnUrl(editImage.key, cdnBaseUrl)}
                    alt={editImage.alt || editImage.title || ""}
                    style={{ width: "100%", height: 220, objectFit: "cover", borderRadius: 12 }}
                  />
                </Col>

                <Col md={7}>
                  <Form.Group className="mb-3">
                    <Form.Label>Title</Form.Label>
                    <Form.Control
                      value={editImage.title || ""}
                      onChange={(e) => setEditImage((p) => ({ ...p, title: e.target.value }))}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Alt text</Form.Label>
                    <Form.Control
                      value={editImage.alt || ""}
                      onChange={(e) => setEditImage((p) => ({ ...p, alt: e.target.value }))}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Tag</Form.Label>
                    <Form.Control
                      value={editImage.tag || ""}
                      onChange={(e) => setEditImage((p) => ({ ...p, tag: e.target.value }))}
                    />
                  </Form.Group>

                  <div className="d-flex align-items-center gap-2">
                    <Form.Check
                      type="switch"
                      checked={editImage.enabled ?? true}
                      onChange={(e) => setEditImage((p) => ({ ...p, enabled: e.target.checked }))}
                      label={editImage.enabled ? "Visible" : "Hidden"}
                    />
                  </div>
                </Col>
              </Row>
            ) : null}
          </Modal.Body>

          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                if (!editImage) return;
                updateImage(editImage.idx, {
                  title: editImage.title,
                  alt: editImage.alt,
                  tag: editImage.tag,
                  enabled: editImage.enabled,
                });
                setShowEditModal(false);
                setEditImage(null);
              }}
            >
              Save
            </Button>
          </Modal.Footer>
        </Modal>

        {/* =========================
            Confirm delete modal
        ========================== */}
        <Modal show={confirmDelete.open} onHide={() => setConfirmDelete({ open: false, key: null, idx: null })} centered>
          <Modal.Header closeButton>
            <Modal.Title>Remove image?</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-muted">
            This will remove the image from the gallery. If it’s a CDN key, we will also request deletion from storage.
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-secondary"
              onClick={() => setConfirmDelete({ open: false, key: null, idx: null })}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeleteGalleryImage}>
              Remove
            </Button>
          </Modal.Footer>
        </Modal>
      </Card.Body>
    </Card>
  );
}
